import { useIsRestoring, useQuery } from "@tanstack/react-query";
import { useAccessToken, useTenantData } from "@/hooks";
import { TeachersApi } from "@/openapi/generated";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, createOpenApiConfiguration } from "@/lib/utils";

export interface UseTeachersGetPublicParams {
  page?: number;
  pageSize?: number;
  active?: boolean;
  enabled?: boolean;
}

/**
 * Paginated teachers for public pages - works with or without auth.
 */
export const useTeachersGetPublic = ({
  page = 1,
  pageSize = 1000,
  active = true,
  enabled = true,
}: UseTeachersGetPublicParams = {}) => {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();
  const isRestoring = useIsRestoring();

  const { data, isError, isLoading, isSuccess, isPending, isFetching, refetch, error } =
    useQuery({
      queryKey: [QueryKeys.Teachers, tenantId, "public", page, pageSize, active],
      queryFn: () => {
        const params = { page, pageSize, active, xTenantID: tenantId };
        if (accessToken) {
          const service = createAuthenticatedApi(TeachersApi, accessToken);
          return service.apiTeachersScrollGet(params);
        }
        const service = new TeachersApi(createOpenApiConfiguration());
        return service.apiTeachersScrollGet(params);
      },
      enabled: enabled && tenantId > 0 && !isRestoring,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    });

  return {
    data,
    isError,
    isLoading,
    isSuccess,
    isPending,
    isFetching,
    refetch,
    error,
  };
};
